import { INTEGRATION_DETAIL_QUERY_PARAM, INTEGRATION_DETAIL_REQUEST_QUERY_PARAM } from "@/components/Settings/integrationRouteIntent";
import { getRouteBehavior, normalizeRoutePath } from "@/routeBehavior";
import {
  buildShopifyInstallCallbackUrl,
  isShopifyInstallPage,
  readShopifyInstallContext,
} from "./shopifyInstall";

const STORAGE_KEY = "bustly.shopify.onboarding-install.v1";
const TTL = 30 * 60 * 1000;
export const ONBOARDING_RETURN_KEY = "shopify_onboarding_return";
const RETURN_CONTEXT_KEYS = [
  "ref",
  "referral_entry",
  "invite_token",
  "invite_id",
  "setting_modal",
  "tab",
];
function safeReturnSearch(search: string, scene?: Scope["scene"]): string {
  const params = new URLSearchParams(search);
  const safe = new URLSearchParams();
  const keys = scene === "admin"
    ? [...RETURN_CONTEXT_KEYS, INTEGRATION_DETAIL_QUERY_PARAM, INTEGRATION_DETAIL_REQUEST_QUERY_PARAM]
    : RETURN_CONTEXT_KEYS;
  for (const key of keys)
    if (params.has(key)) safe.set(key, params.get(key)!);
  return safe.toString();
}
export function readShopifyCallbackBoolean(
  params: URLSearchParams,
  keys: string[],
): boolean | undefined {
  for (const key of keys) {
    const raw = params.get(key);
    if (raw === null || raw === "") continue;
    const value = raw.trim().toLowerCase();
    return value === "true" || value === "1"
      ? true
      : value === "false" || value === "0"
        ? false
        : undefined;
  }
  return undefined;
}
export function hasShopifyCallbackError(search: string): boolean {
  const params = new URLSearchParams(search);
  return (
    ["connect_error", "msg", "error"].some((key) => Boolean(params.get(key))) ||
    readShopifyCallbackBoolean(params, ["blockConnect", "block_connect"]) ===
      true
  );
}
type Scope = {
  accountId: string;
  productCode: string;
  workspaceId?: string;
  scene?: "onboarding" | "admin";
  oauthAppId?: "PRIMARY" | "BACKUP";
  returnPath?: string;
};
export type OnboardingInstallIntent = Scope & {
  workspaceId: string;
  createdAt: number;
  phase: "install" | "oauth" | "verified";
  returnSearch: string;
  scene: "onboarding" | "admin";
  oauthAppId: "PRIMARY" | "BACKUP";
  returnPath: string;
  attemptId?: string;
  shop?: string;
};

export function isFullPageShopifyReturn(search: string): boolean {
  const params = new URLSearchParams(search);
  return (
    params.get(ONBOARDING_RETURN_KEY) === "1" &&
    params.get("connect_success") === "shopify"
  );
}
export function beginOnboardingInstall(
  storage: Storage,
  scope: Scope,
  search: string,
  now = Date.now(),
): void {
  if (!scope.accountId || !scope.productCode || !scope.workspaceId)
    throw new Error("Missing onboarding workspace");
  // Preserve safe view and attribution context, never credentials, tickets or one-shot connect intents.
  const returnSearch = safeReturnSearch(search, scope.scene);
  const intent: OnboardingInstallIntent = {
    ...scope,
    scene: scope.scene || "onboarding",
    oauthAppId: scope.oauthAppId || "BACKUP",
    returnPath:
      scope.scene === "admin" &&
      scope.returnPath &&
      getRouteBehavior(scope.returnPath).usesAdminLayout
        ? scope.returnPath
        : scope.scene === "admin"
          ? "/integrations"
          : "/onboarding",
    workspaceId: scope.workspaceId,
    createdAt: now,
    phase: "install",
    returnSearch,
  };
  storage.setItem(STORAGE_KEY, JSON.stringify(intent));
}
export function readOnboardingInstall(
  storage: Storage,
  scope: Scope,
  now = Date.now(),
): OnboardingInstallIntent | null {
  try {
    const value = JSON.parse(storage.getItem(STORAGE_KEY) || "null");
    if (
      !value ||
      value.accountId !== scope.accountId ||
      value.productCode !== scope.productCode ||
      typeof value.workspaceId !== "string" ||
      !value.workspaceId ||
      (scope.workspaceId && scope.workspaceId !== value.workspaceId) ||
      !Number.isFinite(value.createdAt) ||
      value.createdAt > now ||
      now - value.createdAt > TTL ||
      !["install", "oauth", "verified"].includes(value.phase) ||
      typeof value.returnSearch !== "string"
    )
      return null;
    if (
      value.phase !== "install" &&
      (typeof value.attemptId !== "string" ||
        !value.attemptId ||
        typeof value.shop !== "string" ||
        !/^[a-z0-9][a-z0-9-]*\.myshopify\.com$/.test(value.shop))
    )
      return null;
    const scene = value.scene || "onboarding";
    const oauthAppId = value.oauthAppId || "BACKUP";
    const returnPath = value.returnPath || "/onboarding";
    if (
      !["onboarding", "admin"].includes(scene) ||
      !["PRIMARY", "BACKUP"].includes(oauthAppId) ||
      typeof returnPath !== "string" ||
      !returnPath.startsWith("/") ||
      returnPath.startsWith("//") ||
      /[?#\\]/.test(returnPath) ||
      (scene === "admin" && !getRouteBehavior(returnPath).usesAdminLayout)
    )
      return null;
    return {
      ...value,
      scene,
      oauthAppId,
      returnPath,
      returnSearch: safeReturnSearch(value.returnSearch, value.scene),
    };
  } catch {
    return null;
  }
}
export function clearOnboardingInstall(storage: Storage): void {
  storage.removeItem(STORAGE_KEY);
}
export function matchesOnboardingReturn(
  intent: OnboardingInstallIntent,
  search: string,
): boolean {
  const params = new URLSearchParams(search);
  for (const key of ["workspace_id", "resume_workspace_id"])
    if (params.has(key) && params.get(key) !== intent.workspaceId) return false;
  if (isFullPageShopifyReturn(search))
    return (
      intent.phase === "oauth" &&
      Boolean(intent.shop) &&
      Boolean(intent.attemptId) &&
      params.get("workspace_id") === intent.workspaceId &&
      params.get("shopify_connect_attempt") === intent.attemptId
    );
  return (
    Boolean(readShopifyInstallContext(search)) &&
    (intent.phase === "install" || intent.phase === "oauth")
  );
}
export function readOnboardingWorkspaceReturn(
  storage: Storage,
  scope: Scope,
  pathname: string,
  search: string,
): OnboardingInstallIntent | null {
  const intent = readOnboardingInstall(storage, scope);
  if (!intent) return null;
  if (isShopifyInstallPage(pathname) && matchesOnboardingReturn(intent, search))
    return intent;
  const params = new URLSearchParams(search);
  if (
    (intent.phase === "verified" || matchesOnboardingReturn(intent, search)) &&
    ((intent.scene === "onboarding" &&
      getRouteBehavior(pathname).pageKind === "onboarding" &&
      params.get("resume_workspace_id") === intent.workspaceId &&
      params.get("resume_step") === "connect") ||
      (intent.scene === "admin" &&
        normalizeRoutePath(pathname) ===
          normalizeRoutePath(intent.returnPath) &&
        params.get("workspace_id") === intent.workspaceId))
  )
    return intent;
  return null;
}
export function readBrowserOnboardingWorkspaceReturn(
  scope: Scope,
  pathname: string,
  search: string,
): OnboardingInstallIntent | null {
  if (
    !isShopifyInstallPage(pathname) &&
    !new URLSearchParams(search).has("resume_workspace_id") &&
    !new URLSearchParams(search).has("workspace_id")
  )
    return null;
  try {
    return readOnboardingWorkspaceReturn(
      window.sessionStorage,
      scope,
      pathname,
      search,
    );
  } catch {
    return null;
  }
}
export function buildOnboardingInstallCallback(
  origin: string,
  workspaceId: string,
  attemptId: string,
  scene: "onboarding" | "admin" = "onboarding",
): string {
  const url = new URL(
    buildShopifyInstallCallbackUrl(origin, workspaceId, attemptId),
  );
  url.searchParams.set(ONBOARDING_RETURN_KEY, "1");
  url.searchParams.set("shopify_connection_scene", scene);
  return url.toString();
}
function assertLive(signal: AbortSignal) {
  if (signal.aborted) throw new Error("Installation cancelled");
}
export function assertInstallWorkspace(
  workspace: { id?: string; status?: string; currentUserRole?: string | null },
  workspaceId: string,
): void {
  if (
    workspace?.id !== workspaceId ||
    !["ACTIVE", "SUSPENDED"].includes(workspace.status || "") ||
    !["OWNER", "ADMIN", "MEMBER"].includes(
      (workspace.currentUserRole || "").toUpperCase(),
    )
  )
    throw new Error("Onboarding workspace unavailable");
}
export async function runOnboardingInstall(options: {
  storage: Storage;
  intent: OnboardingInstallIntent;
  ticket: string;
  origin: string;
  attemptId: string;
  signal: AbortSignal;
  resolve: (
    ticket: string,
  ) => Promise<{ shop_domain: string; oauth_app_id: string }>;
  workspace: () => Promise<{
    id?: string;
    status?: string;
    currentUserRole?: string | null;
  }>;
  connect: (request: {
    install_context: string;
    shop_domain: string;
    redirect_url: string;
    connection_scene: "onboarding" | "admin";
  }) => Promise<{
    authorizationUrl?: string;
    blockConnect?: boolean;
    alreadyConnected?: boolean;
    success?: boolean;
    restrictionState?: string;
  }>;
  redirect: (url: string) => void;
}): Promise<{ alreadyConnected: boolean }> {
  const { signal, intent } = options;
  const workspace = await options.workspace();
  assertLive(signal);
  assertInstallWorkspace(workspace, intent.workspaceId);
  const context = await options.resolve(options.ticket);
  assertLive(signal);
  if (
    context.oauth_app_id !== intent.oauthAppId ||
    !/^[a-z0-9][a-z0-9-]*\.myshopify\.com$/.test(context.shop_domain)
  )
    throw new Error("Invalid Shopify installation context");
  const response = await options.connect({
    install_context: options.ticket,
    shop_domain: context.shop_domain,
    redirect_url: buildOnboardingInstallCallback(
      options.origin,
      intent.workspaceId,
      options.attemptId,
      intent.scene,
    ),
    connection_scene: intent.scene,
  });
  assertLive(signal);
  if (response.blockConnect && response.alreadyConnected !== true) throw new Error("Shopify connection restricted");
  const correlated = {
    ...intent,
    phase: "oauth",
    attemptId: options.attemptId,
    shop: context.shop_domain,
  };
  if (
    response.alreadyConnected === true ||
    response.restrictionState === "already_connected"
  ) {
    options.storage.setItem(STORAGE_KEY, JSON.stringify(correlated));
    return { alreadyConnected: true };
  }
  const url = new URL(response.authorizationUrl || "");
  if (
    url.protocol !== "https:" ||
    url.hostname !== context.shop_domain ||
    url.port ||
    url.username ||
    url.password ||
    url.pathname !== "/admin/oauth/authorize"
  )
    throw new Error("Invalid Shopify authorization URL");
  options.storage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      ...intent,
      phase: "oauth",
      attemptId: options.attemptId,
      shop: context.shop_domain,
    }),
  );
  options.redirect(url.toString());
  return { alreadyConnected: false };
}
export function hasExpectedShopifyInstallation(
  value: unknown,
  shop: string,
  oauthAppId: "PRIMARY" | "BACKUP" = "BACKUP",
): boolean {
  const rows = (
    value as {
      shopify?: Array<{
        connected?: boolean;
        mappings?: Array<{
          oauth_app_id?: string;
          shop_domain?: string;
          status?: string;
        }>;
      }>;
    } | null
  )?.shopify;
  return (
    Array.isArray(rows) &&
    rows.some(
      (row) =>
        row?.connected === true &&
        Array.isArray(row.mappings) &&
        row.mappings.some(
          (mapping) =>
            mapping.oauth_app_id === oauthAppId &&
            mapping.shop_domain === shop &&
            mapping.status === "ACTIVE",
        ),
    )
  );
}
export function buildOnboardingInstallResumeRoute(
  intent: OnboardingInstallIntent,
): string {
  const params = new URLSearchParams(safeReturnSearch(intent.returnSearch, intent.scene));
  if (intent.scene === "admin") {
    params.set("workspace_id", intent.workspaceId);
    return `${intent.returnPath}?${params}`;
  }
  params.set("resume_workspace_id", intent.workspaceId);
  params.set("resume_step", "connect");
  return `/onboarding?${params}`;
}
export async function verifyOnboardingInstallation(
  intent: OnboardingInstallIntent,
  load: () => Promise<unknown>,
  signal: AbortSignal,
  wait: (ms: number) => Promise<void> = (ms) =>
    new Promise((resolve) => setTimeout(resolve, ms)),
): Promise<string> {
  if (intent.phase !== "oauth" || !intent.shop || !intent.attemptId)
    throw new Error("Missing authorization correlation");
  for (let attempt = 0; attempt < 5; attempt++) {
    assertLive(signal);
    const value = await load();
    assertLive(signal);
    if (hasExpectedShopifyInstallation(value, intent.shop, intent.oauthAppId)) {
      return buildOnboardingInstallResumeRoute(intent);
    }
    if (attempt < 4) await wait(1000);
  }
  throw new Error("Shopify installation not confirmed");
}
/** Popup flow: bind the originating tab's intent to the popup attempt before full-page verification. */
export function correlateOnboardingInstall(
  storage: Storage,
  intent: OnboardingInstallIntent,
  attemptId: string,
  shop: string,
): OnboardingInstallIntent {
  if (!attemptId || !/^[a-z0-9][a-z0-9-]*\.myshopify\.com$/.test(shop))
    throw new Error("Invalid Shopify installation correlation");
  const correlated: OnboardingInstallIntent = {
    ...intent,
    phase: "oauth",
    attemptId,
    shop,
  };
  storage.setItem(STORAGE_KEY, JSON.stringify(correlated));
  return correlated;
}
export function markOnboardingInstallVerified(
  storage: Storage,
  intent: OnboardingInstallIntent,
): void {
  storage.setItem(
    STORAGE_KEY,
    JSON.stringify({ ...intent, phase: "verified" }),
  );
}

/** Keep recovery metadata through connect-step hydration, then retire it on exit. */
export function retireCompletedOnboardingInstall(
  storage: Storage,
  scope: Scope,
  pathname: string,
  search: string,
): void {
  const intent = readOnboardingInstall(storage, scope);
  if (
    intent?.phase === "verified" &&
    !readOnboardingWorkspaceReturn(storage, scope, pathname, search)
  )
    clearOnboardingInstall(storage);
}

/** Carry the signed return only in the URL until the connect-step owner consumes it. */
export function buildOnboardingInstallProcessingRoute(
  intent: OnboardingInstallIntent,
  search: string,
): string {
  const url = new URL(
    buildOnboardingInstallResumeRoute(intent),
    "https://bustly.ai",
  );
  const incoming = new URLSearchParams(search);
  for (const key of [
    "shopify_install_context",
    "shopify_connection_scene",
    ONBOARDING_RETURN_KEY,
    "connect_success",
    "workspace_id",
    "shopify_connect_attempt",
    "connect_error",
    "msg",
    "error",
    "blockConnect",
    "block_connect",
  ]) {
    if (incoming.has(key)) url.searchParams.set(key, incoming.get(key)!);
  }
  return `${url.pathname}${url.search}`;
}

export function isInlineShopifyReturn(search: string): boolean {
  return Boolean(
    readShopifyInstallContext(search) ||
    isFullPageShopifyReturn(search) ||
    new URLSearchParams(search).get("shopify_return_error") === "1",
  );
}

export function buildFailedOnboardingInstallRoute(search: string): string {
  const incoming = new URLSearchParams(search);
  const params = new URLSearchParams(safeReturnSearch(search));
  const workspaceId = incoming.get("workspace_id");
  if (incoming.get("shopify_connection_scene") === "admin") {
    const recovery = new URLSearchParams();
    if (workspaceId) recovery.set("workspace_id", workspaceId);
    recovery.set("shopify_return_error", "1");
    return `/integrations?${recovery}`;
  }
  if (workspaceId) {
    params.set("resume_workspace_id", workspaceId);
    params.set("resume_step", "connect");
  }
  params.set("shopify_return_error", "1");
  return `/onboarding?${params}`;
}
